import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Box,
  ListItem,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';

import Button from '@/components/button';
import { useAppSelector } from '@/hooks/redux';
import { getMonthsSince } from '@/utils/datesAndTime/datesAndTime';
import getRoute from '@/utils/getRoute';

interface AccountInfoMenuProps {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  handleClose: () => void;
}

interface SubmenuItemProps {
  label: string;
  children: React.ReactNode;
}

const SubmenuItem = ({ label, children }: SubmenuItemProps) => {
  const anchorRef = useRef<HTMLLIElement | null>(null);
  const [open, setOpen] = useState<boolean>(false);

  return (
    <MenuItem
      ref={anchorRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      sx={{ justifyContent: 'space-between' }}
      disableRipple
    >
      <Typography variant="body2">{label}</Typography>
      <ChevronRightIcon fontSize="small" />
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setOpen(false)}
        sx={{
          pointerEvents: 'none',
          '& .MuiPaper-root': { pointerEvents: 'auto' }
        }}
      >
        {children}
      </Menu>
    </MenuItem>
  );
};

const AccountInfoMenu = ({
  isOpen,
  anchorEl,
  handleClose
}: AccountInfoMenuProps) => {
  const navigate = useNavigate();

  const user = useAppSelector((state) => state.user.currentUser);

  const handleLogout = () => {
    navigate(getRoute.auth.LOGOUT());
  };

  return (
    <Menu anchorEl={anchorEl} open={isOpen} onClose={handleClose}>
      <ListSubheader>Account</ListSubheader>
      <ListItem>
        <ListItemText>
          <Typography fontWeight={600}>{user.workspaceName}</Typography>
          <Typography>{user.name}</Typography>
          <Typography variant="body2" color="textSecondary">
            {user.email}
          </Typography>
        </ListItemText>
      </ListItem>

      {user.workspaceStorageTerms && (
        <SubmenuItem label="Storage Terms">
          {Object.entries(user.workspaceStorageTerms).map(([key, value]) => (
            <Tooltip
              key={key}
              title="If this term is no longer enough, please contact us."
              placement="right"
              arrow
            >
              <MenuItem
                key={key}
                disableRipple
                sx={{ cursor: 'default', '&:hover': { background: 'none' } }}
              >
                <Typography variant="caption" textTransform="capitalize">
                  <Box component="span" mr={0.5} fontWeight={600}>
                    {key}:
                  </Box>
                  {`${getMonthsSince(value)} months`}
                </Typography>
              </MenuItem>
            </Tooltip>
          ))}
        </SubmenuItem>
      )}

      <Stack direction="row" justifyContent="flex-end" paddingX={1}>
        <Button
          sx={{ margin: '8px' }}
          variant="text"
          text="Log Out"
          onClick={handleLogout}
        />
      </Stack>
    </Menu>
  );
};

export default AccountInfoMenu;
